import type { FormikErrors, FormikState } from 'formik';
import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';

import type { CPScheduleDateRange, SetCPScheduleFormValues } from '../types';
import { CpScheduleFormFields } from '../types';
import { validateScheduleForm } from '../validation/scheduleForm';

type UseScheduleFormState = {
  values: SetCPScheduleFormValues;
  isDirty: boolean;
  hasErrors: boolean;
  isValid: boolean;
  resetForm: (nextState?: Partial<FormikState<SetCPScheduleFormValues>> | undefined) => void;
  resetRangeErrors: () => void;
  validateAndSetErrors: () => boolean;
};

export const useScheduleFormState = (): UseScheduleFormState => {
  const { values, dirty, errors, resetForm, setErrors } = useFormikContext<SetCPScheduleFormValues>();

  const hasErrors = useMemo(() => {
    const rangesErrors = errors[CpScheduleFormFields.Ranges];
    const timezoneError = errors[CpScheduleFormFields.Timezone];

    if (timezoneError) return true;

    if (Array.isArray(rangesErrors)) {
      return rangesErrors.some((error: string | FormikErrors<CPScheduleDateRange>) => Object.keys(error).length > 0);
    }

    return Boolean(rangesErrors);
  }, [errors]);

  const validateAndSetErrors = useCallback((): boolean => {
    const validationErrors = validateScheduleForm(values);

    const isValid = Object.keys(validationErrors).length === 0;

    if (!isValid) {
      setErrors(validationErrors);
    }

    return isValid;
  }, [values, setErrors]);

  const resetRangeErrors = useCallback((): void => {
    setErrors({
      ...errors,
      [CpScheduleFormFields.Ranges]: undefined,
    });
  }, [errors, setErrors]);

  return {
    values,
    isDirty: dirty,
    hasErrors,
    isValid: dirty && !hasErrors,
    resetForm,
    validateAndSetErrors,
    resetRangeErrors,
  };
};
